import React, { useState, useEffect } from 'react';
import { ChevronDown } from "lucide-react";
import { useNavigate } from 'react-router-dom';

const QUOTE_ID = 'V5rw6uja5';

const Accordion = ({ title, children, onOpen }) => {
	const [isOpen, setIsOpen] = useState(false);
	const handleToggle = () => {
		const next = !isOpen;
		setIsOpen(next);

		if (next && onOpen) {
			onOpen(title);
		}
	};
	return (
		<div className={`accordion ${isOpen ? "open" : ""}`}>
			<button className="accordion-trigger" onClick={handleToggle}>
				<h3>{title}</h3>
				<ChevronDown className={`accordion-icon ${isOpen ? "rotated" : ""}`} />
			</button>
			{isOpen && <>{children}</>}
		</div>
	);
};

function V5rw6uja5() {
	const navigate = useNavigate();
	const [input, setInput] = useState('');
	const [unlocked, setUnlocked] = useState(false);
	const [error, setError] = useState('');
	const [suppressNotifications, setSuppressNotifications] = useState('');

	const [quoteData, setQuoteData] = useState(null);

	useEffect(() => {
		fetch(`/api/quoteRoute/${QUOTE_ID}`)
			.then(r => r.json())
			.then(setQuoteData);
	}, []);

	const unlock = (suppressNotification = false, meta = {}) => {
		setUnlocked(true);
		setSuppressNotifications(suppressNotification);

		if (suppressNotification) return;

		fetch('/api/quoteRoute/viewed', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				quoteId: QUOTE_ID,
				unlockMethod: meta.method,
				unlockValue: meta.value
			})
		}).catch(() => { });
	};

	const trackAccordionOpen = (sectionTitle) => {
		if (suppressNotifications) return;
		fetch('/api/quoteRoute/interaction', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				quoteId: QUOTE_ID,
				type: 'accordion_open',
				section: sectionTitle
			})
		}).catch(() => { });
	};

	if (!unlocked) {
		return (
			<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
				<div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '2rem', width: '100%', maxWidth: '380px', textAlign: 'center' }}>
					<img src='/transparent-logo-min.png' alt='Logo' style={{ width: '100%', marginBottom: '1rem' }} />
					<h2 style={{ marginBottom: '0.25rem' }}>Your Quote</h2>
					<p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>North Florida Home Repair & Maintenance</p>
					<input
						type="text"
						placeholder="Enter your access code"
						value={input}
						onChange={e => setInput(e.target.value)}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								const cleaned = input.replace(/\D/g, '') || input.trim();
								const normalize = v => v?.replace(/\D/g, '') || v?.trim();

								const phone1 = normalize(quoteData?.clientPhone);
								const phone2 = normalize(quoteData?.clientPhone2);
								const password = normalize(quoteData?.customPassword);

								if (cleaned === '1131') {
									unlock(true, { method: 'override', value: cleaned });
								} else if (cleaned === phone1) {
									unlock(false, { method: 'clientPhone', value: cleaned });
								} else if (cleaned === phone2) {
									unlock(false, { method: 'clientPhone2', value: cleaned });
								} else if (cleaned === password) {
									unlock(false, { method: 'customPassword', value: cleaned });
								} else {
									setError('Incorrect. Try again.');
								}
							}
						}}
						style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem', width: '100%', boxSizing: 'border-box', marginBottom: '0.5rem' }}
					/>
					{error && <p style={{ color: '#ef4444', fontSize: '0.875rem', margin: '0 0 0.5rem' }}>{error}</p>}
					<button
						onClick={() => {
							const cleaned = input.replace(/\D/g, '') || input.trim();
							const normalize = v => v?.replace(/\D/g, '') || v?.trim();

							const phone1 = normalize(quoteData?.clientPhone);
							const phone2 = normalize(quoteData?.clientPhone2);
							const password = normalize(quoteData?.customPassword);

							if (cleaned === '1131') {
								unlock(true, { method: 'override', value: cleaned });
							} else if (cleaned === phone1) {
								unlock(false, { method: 'clientPhone', value: cleaned });
							} else if (cleaned === phone2) {
								unlock(false, { method: 'clientPhone2', value: cleaned });
							} else if (cleaned === password) {
								unlock(false, { method: 'customPassword', value: cleaned });
							} else {
								setError('Incorrect. Try again.');
							}
						}}
						style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.65rem 1.25rem', fontSize: '0.95rem', cursor: 'pointer', width: '100%' }}
					>
						View quote
					</button>
				</div>
			</div>
		);
	}
	return (
		<div className='app'>
			<main className='page' style={{ paddingTop: 0 }}>
				<section id='about' className='section section-about'>
					<div className='hero'>
						<img
							src='/jax_small.jpg'
							alt='North Florida Home Repair & Maintenance'
							className='hero-image'
						/>

						<div className='hero-overlay'>
							<div className='hero-text-box'>
								<div className='hero-title-main'>North Florida</div>
								<div className='hero-title-sub'>Home Repair & Maintenance</div>
							</div>
						</div>
					</div>

					<div className='custom-quote'>
						<div className="quote-subsection" style={{ borderBottom: '2px solid #111827' }}>
							<p>
								Thank you again for having me over and walking me through everything. I've had a chance to go through my notes and measurements for the projects we discussed.
							</p>
							<p>
								As promised, here's a full breakdown of quotes for each project. I've included notes alongside several items to explain my thinking.
							</p>
							<p>
								Each project is quoted separately so you can pick and choose what you want to move forward with, and in whatever order works best for you. Just tell me when you're ready for any given project and I'll email a formal quote for you to accept.
							</p>
						</div>

						<Accordion title="Project 1: Guest Room Closet" onOpen={trackAccordionOpen}>
							<div className="quote-section">

								<div className='quote-subsection'>
									<div className="project-description">The existing wire shelving unit was never anchored to studs, resulting in it pulling away from the wall. I'll remove it completely, patch the drywall, and install two full-width wood shelves with hanger rods, giving you proper double-hang clothing storage across the full span of the closet.</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Scope of work</h3>
									<div className="white-card">
										<ul className="scope-list">
											<li><strong>Demolition:</strong> Remove the collapsed wire shelving and existing hardware</li>
											<li><strong>Wall Prep:</strong> Patch and blend all drywall damage left behind</li>
											<li><strong>Shelving:</strong> Locate studs and install two full-width shelves (upper and lower) with proper blocking</li>
											<li><strong>Hanger Rods:</strong> Mount hanger rods on both levels with clearance for coats and long garments</li>
											<li><strong>Finishing:</strong> Prime and paint shelves (two coats primer, two coats semi-gloss). Final leveling, clean up, and haul away all debris</li>
										</ul>
										<div className='time-expectation'><strong>Time Expectation:</strong> 7 hours</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Material options</h3>

									<div className="material-choice-grid">
										<div className="white-card">
											<div className="white-card-title">Solid wood</div>
											<div className="white-card-comment">Solid poplar boards- no finger joints or engineered core. Takes primer and paint the same as primed pine, but the surface is denser and holds up better over time. Wast decades without any reservations.</div>
											<div className="white-card-comment"><a href="https://www.homedepot.com/p/3-4-in-x-11-1-4-in-x-Random-Length-Kiln-Dried-Poplar-Decking-Board-106440/206089817" target="_blank">Material cost: $198</a> <span className="material-cost-note">+ paint & primer (~$30)</span></div>
										</div>
										<div className="white-card">
											<div className="white-card-title">Primed pine</div>
											<div className="white-card-comment">Engineered pine boards. Since they come white from the factory and your trim is white, one coat of semi-gloss is all it needs. Durable, easy to touch up, and can be repainted any color down the road.</div>
											<div className="white-card-comment"><a href="https://www.homedepot.com/p/1-in-x-12-in-x-8-ft-Primed-Pine-Finger-Joint-Board-PFJB1128/203422125" target="_blank">Material cost: $60</a> <span className="material-cost-note">+ paint (~$15)</span></div>
										</div>
										<div className="white-card">
											<div className="white-card-title">Melamine wood panels</div>
											<div className="white-card-comment">Comes pre-finished in white, so no painting needed, which is its main practical advantage. The tradeoff is that if it chips or scratches down the road, it typically can't be touched up and may need full replacement.</div>
											<div className="white-card-comment"><a href="https://www.homedepot.com/p/Veranda-White-Melamine-Wood-Shelf-11-75-in-D-x-97-in-L-1605509/202088761" target="_blank">Material cost: $46</a></div>
										</div>
									</div>

								</div>

								<hr />

								<div className='quote-subsection'>
									<div className="white-card">
										<div className="white-card-title">Support brackets</div>
										<div className="white-card-comment">The options below are rated for more than enough weight for this application. The choice is purely aesthetic. You'll need 8 total.</div>
										<ul className="material-card-list">
											<li><a href="https://www.homedepot.com/p/Everbilt-8-in-x-12-4-in-x-1-05-in-White-Steel-Shelf-and-Rod-Bracket-Heavy-Duty-300-lbs-EH-WSTHDUS-318/300262526" target="_blank">12" bracket: $92 total</a></li>
											<li><a href="https://www.homedepot.com/p/Crates-Pallet-12-in-White-Steel-Shelf-Bracket-69116/318948367" target="_blank">12" decorative bracket: $143 total</a></li>
										</ul>
										<div className="white-card-comment"><b>Note: The decorative bracket will NOT fit on the melamine wood.</b> It arguably looks the best and most intentional, but also consider the brackets are pretty hard to see once the closet is full anyway.</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<div className="white-card">
										<div className="white-card-title">Required Essentials</div>
										<div className="white-card-comment">To complete the build, I've also factored in the following items where there aren't specific choices to make:</div>
										<ul className="material-card-list">
											<li><strong>Heavy-Duty Closet Rods:</strong> Two 8ft brushed metal rods</li>
											<li><strong>Rod Flanges:</strong> Heavy-duty end caps</li>
											<li><strong>Structural Hardware</strong> & misc consumables</li>
										</ul>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Pricing</h3>
									<div className="quote-pricing-block">
										<div className="pricing-row">
											<span className="pricing-label">Labor (flat rate):</span>
											<span className="pricing-value">$275</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials range:</span>
											<span className="pricing-value">$243 - $491</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">$518 - $766</span>
										</div>
									</div>
									<div className="pricing-note">Materials are billed at cost. The range shown reflects the lowest and highest cost choices.</div>
								</div>

							</div>
						</Accordion>

						<Accordion title="Project 2: Kitchen Island/Bar" onOpen={trackAccordionOpen}>
							<div className="quote-section">

								<div className='quote-subsection'>
									<div className="project-description">
										So I thought more about this project from a design/aesthetic perspective, and I've quoted three options below- the tile you originally selected, and two shiplap alternatives I want to make a case for.
									</div>
								</div>

								<hr />

								<h3 className="section-label">Option comparison</h3>

								<div className='quote-subsection'>
									<div className="material-choice-grid">
										<div className="white-card">
											<div className="white-card-title">Porcelain tile</div>
											<div className="white-card-comment"><a href="https://www.homedepot.com/pep/Marazzi-Montagna-Wood-Vintage-Chic-6-in-x-24-in-Porcelain-Floor-and-Wall-Tile-14-53-sq-ft-case-ULRW624HD1PR/205473903" target="_blank">The tile you picked out</a> installed horizontally across all faces of the island. Schluter metal edge strips on all 8 external corners. Existing base and top trim removed, repainted, and reinstalled with new corner pieces cut to fit the updated depth.</div>
											<ul className="option-card-list">
												<li><strong>Appearance:</strong> Bold, decorative, high contrast against the cabinets. Could clash with the backsplash</li>
												<li><strong>Durability:</strong> Extremely durable and water resistant</li>
												<li><strong>Tradeoff:</strong> If a tile ever cracks or chips, matching replacements may be hard or impossible to source down the road</li>
											</ul>
										</div>
										<div className="white-card">
											<div className="white-card-title">Shiplap - engineered pine</div>
											<div className="white-card-comment">Horizontal nickel-gap primed pine boards finished in a <em>white</em> cabinet enamel, running across all faces of the island. The narrow returns get the same treatment, with a small trim piece capping the interior edge cleanly. Existing top cap stays or gets swapped to match.</div>
											<ul className="option-card-list">
												<li><strong>Appearance:</strong> Clean, intentional, ties directly into the existing trim and base</li>
												<li><strong>Durability:</strong> Cabinet-grade enamel makes it hard, scrubbable, and moisture resistant. Easy to touch up if ever needed</li>
												<li><strong>Tradeoff:</strong> Finger-joint construction means the surface is slightly less dense than solid wood, but holds paint well and is not noticeable once finished</li>
											</ul>
										</div>
										<div className="white-card">
											<div className="white-card-title">Shiplap - solid wood</div>
											<div className="white-card-comment">Same horizontal nickel-gap layout as the engineered option, but using solid hardwood boards. Requires a full finishing schedule (5 sanding passes, 2 coats primer, 2 coats paint) before install, which adds significant labor.</div>
											<ul className="option-card-list">
												<li><strong>Appearance:</strong> Identical to the engineered pine once painted</li>
												<li><strong>Durability:</strong> Denser surface holds up better over time and takes touch-ups exceptionally well</li>
												<li><strong>Tradeoff:</strong> Meaningfully more labor-intensive than the primed pine option due to the full finish schedule required</li>
											</ul>
										</div>
									</div>

									<div className="subsection-comment">
										I'd go with the shiplap. The island is surrounded by dark cabinets and a busy granite top. White horizontal boards would complement all of that instead of competing with it. It also ties the island visually to the front room treatment if you go that route, giving the whole main floor a cohesive thread. The tile you picked matches the overall design scheme of the kitchen, but I think it works better as a backsplash than wrapped around a furniture-scale piece. And, as a practical decision, white shiplap is significantly less expensive and less disruptive to install.
										<br /><br />
										Note: I'd also recommend keeping the island shiplap white even if the front room shiplap ends up painted. The island shiplap needs to stay neutral. Matching colors across two rooms that visible to each other simultaneously would be a lot.
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Scope of work</h3>

									<div className="material-choice-grid">
										<div className="white-card">
											<div className="white-card-title">If tile:</div>
											<ul className="scope-list" style={{ marginTop: 0 }}>
												<li><strong>Trim removal:</strong> Carefully remove all base and top trim pieces and set aside</li>
												<li><strong>Surface prep:</strong> Clean and apply tile adhesion primer to all drywall surfaces</li>
												<li><strong>Tile setting:</strong> Set tiles horizontally using high-bond non-sag polymer thinset</li>
												<li><strong>Edge trim:</strong> Install Schluter metal edge strips on all 8 external vertical corners</li>
												<li><strong>Grouting:</strong> Apply stain-resistant grout at all joints in a color matched to the tile</li>
												<li><strong>Trim:</strong> Cut new corner pieces to fit the updated depth, repaint all base trim, and reinstall</li>
												<li><strong>Outlet:</strong> Install electrical box extender to bring outlet flush with new tile surface</li>
												<li><strong>Cleanup:</strong> Full cleanup including grout haze removal and tile polish</li>
											</ul>
											<div className="time-expectation"><strong>Time expectation:</strong> 3 days (18-20 hours)</div>
										</div>
										<div className="white-card">
											<div className="white-card-title">If shiplap (pine boards):</div>
											<ul className="scope-list" style={{ marginTop: 0 }}>
												<li><strong>Surface prep:</strong> Clean and lightly sand all drywall faces</li>
												<li><strong>Board installation:</strong> Install horizontal nickel-gap shiplap across all faces with mitered corners at all 8 angles</li>
												<li><strong>Cabinet returns:</strong> Run shiplap across both narrow cabinet-side strips, cap the interior edge with trim</li>
												<li><strong>Outlet:</strong> Install electrical box extender to bring outlet flush with new shiplap surface</li>
												<li><strong>Finishing:</strong> Fill nail holes, caulk all edges, wood filler at all miters, sand to 220 grit, apply two finish coats of cabinet-grade enamel</li>
												<li><strong>Cleanup:</strong> Full cleanup and haul away all cutoffs and debris</li>
											</ul>
											<div className="time-expectation"><strong>Time expectation:</strong> 3 days (10-12 hours), drying time required</div>
										</div>
										<div className="white-card">
											<div className="white-card-title">If shiplap (solid wood):</div>
											<p>Similar to pine, with these changes:</p>
											<ul className="scope-list" style={{ marginTop: 0 }}>
												<li><strong>Board milling:</strong> Mill rabbet along one edge of each board to create the nickel-gap reveal (primed pine comes pre-milled; solid poplar requires this step)</li>
												<li><strong>Board prep:</strong> Sand boards, apply two coats of primer with sanding between coats</li>
												<li><strong>Finishing:</strong> Additional sanding to 320 grit</li>
											</ul>
											<div className="time-expectation"><strong>Time expectation:</strong> 3 days (18-22 hours), drying time required</div>
										</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Materials estimate</h3>
									<div className="material-choice-grid">
										<div className="white-card">
											<div className="white-card-title">If tile:</div>
											<ul className="material-card-list">
												<li>Tile (3 cases): $126</li>
												<li>Thinset mortar: $25</li>
												<li>Grout: $20</li>
												<li>Schluter metal edge trim: $45</li>
												<li>Tile adhesion primer: $20</li>
												<li>Replacement trim corner pieces: $20-30</li>
												<li>Paint for base trim: $15</li>
												<li>Wet saw rental (1 day): $65</li>
												<li>Electrical box extender: $5</li>
												<li>Misc consumables: $30</li>
											</ul>
										</div>
										<div className="white-card">
											<div className="white-card-title">If shiplap:</div>
											<ul className="material-card-list">
												<li>Engineered pine boards: <a href="https://www.homedepot.com/p/ARAUCO-9-16-in-x-5-1-4-in-x-12-ft-Radiata-Pine-Nickel-Gap-Ship-Lap-Board-1186661/300803814" target="_blank">$110</a></li>
												<li>Poplar boards: <a href="https://www.homedepot.com/p/Poplar-Board-Common-1-in-x-6-in-x-R-L-Actual-0-75-in-x-5-5-in-x-R-L-21065/302006547" target="_blank">$350</a></li>
												<li>Maple boards: <a href="https://www.homedepot.com/p/Swaner-Hardwood-1-in-x-6-in-x-8-ft-Maple-S4S-Board-2-Pack-OL04051696ME/310362079" target="_blank">$500</a></li>
												<li>Interior return trim: $10-30</li>
												<li>Construction adhesive + finish nails: $20</li>
												<li>Primer + white paint: $25-60</li>
												<li>Electrical box extender: $5</li>
												<li>Misc (caulk, wood filler, sandpaper): $15</li>
											</ul>
										</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Pricing</h3>
									<div className="quote-pricing-block">
										<b>Tile</b>
										<div className="pricing-row">
											<span className="pricing-label">Labor (flat rate):</span>
											<span className="pricing-value">$1,300</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials:</span>
											<span className="pricing-value">~$375</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">$1,675</span>
										</div>
									</div>
									<div className="quote-pricing-block">
										<b>Shiplap - engineered pine</b>
										<div className="pricing-row">
											<span className="pricing-label">Labor (flat rate):</span>
											<span className="pricing-value">$780</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials:</span>
											<span className="pricing-value">~$185</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">$965</span>
										</div>
									</div>
									<div className="quote-pricing-block">
										<b>Shiplap - solid wood</b>
										<div className="pricing-row">
											<span className="pricing-label">Labor (flat rate):</span>
											<span className="pricing-value">$1,430</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials:</span>
											<span className="pricing-value">~$480 - $630</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">$1,910 - $2,060</span>
										</div>
									</div>
									<div className="pricing-note">Pricing reflects a high-end, furniture-grade finish. This includes meticulous surface prep, mitered joinery, and multi-stage sanding/painting to ensure the island looks like an integrated part of your cabinetry rather than a DIY add-on. Materials are billed at exact cost.</div>
								</div>

							</div>
						</Accordion>

						<Accordion title="Project 3: Drywall & Wall Repairs" onOpen={trackAccordionOpen}>
							<div className="quote-section">

								<div className='quote-subsection'>
									<div className="project-description">
										There are a number of wall repairs needed throughout the house- damaged corner bead, a hole behind a door, a bathroom patch that needs redoing, and several spots where previous patch attempts are still visible. I'll also be tackling the front room wall with the command strips, repairing any adhesive damage and repainting the entire wall for a clean, consistent finish.
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Scope of work</h3>
									<div className="white-card">
										<ul className="scope-list">
											<li><strong>Corner bead:</strong> Repair all damaged plastic corner bead sections throughout the house. Mud, sand, and blend</li>
											<li><strong>Door hole:</strong> Patch the ~2" hole, skim, sand, and touch up</li>
											<li><strong>Bathroom patch:</strong> Sand down the existing patch, skim coat, sand, and finish properly</li>
											<li><strong>Misc wall spots:</strong> Address 3-5 additional spots where previous patch attempts are visible</li>
											<li><strong>Front room wall:</strong> Remove all command strips and adhesive, repair any wall damage underneath, prime affected areas, and repaint the entire wall</li>
											<li><strong>Touch up paint:</strong> Spot prime and paint all repaired areas throughout to match existing walls</li>
										</ul>
										<div className="time-expectation"><strong>Time expectation:</strong> 2 days (9-10 hours), overnight required for drying</div>
									</div>
									<div className="subsection-comment">
										Note: As quoted, this is an aesthetic restoration. If any corner sections are found to be structurally detached from the framing, they may require full replacement to ensure long-term durability. This takes much longer to fix and will incur an additional labor charge of $30 per corner.
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Materials estimate</h3>
									<div className="white-card">
										<ul className="material-card-list">
											<li>Vinyl corner bead: $15</li>
											<li>Joint compound: $15</li>
											<li>Mesh patch: $5</li>
											<li>Primer: $15</li>
											<li>Paint: $70</li>
											<li>Misc consumables: $15</li>
										</ul>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Pricing</h3>
									<div className="quote-pricing-block">
										<div className="pricing-row">
											<span className="pricing-label">Labor (flat rate):</span>
											<span className="pricing-value">$550</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials:</span>
											<span className="pricing-value">~$135</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">$685</span>
										</div>
									</div>
									<div className="pricing-note">Materials are billed at exact cost.</div>
								</div>

							</div>
						</Accordion>

						<Accordion title="Project 4: Stairwell" onOpen={trackAccordionOpen}>
							<div className="quote-section">

								<div className='quote-subsection'>
									<div className="project-description">
										The stairwell walls require significant restoration due to high-traffic wear. I'll perform a deep clean to ensure paint adhesion, followed by a full repaint. I'll also perform a structural/aesthetic restoration of the handrail, which has significant surface damage.
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Scope of work</h3>
									<div className="white-card">
										<ul className="scope-list">
											<li><strong>Walls (Prep):</strong> Chemical degreasing of all wall surfaces to remove oils and grime, followed by scuff-sanding and priming of affected areas</li>
											<li><strong>Walls (Finish):</strong> Two coats of high-durability, scrubbable wall paint</li>
											<li><strong>Handrail:</strong> Mechanical sanding to remove failing finish, wood filling of all gouges/dents, priming, and two coats of furniture-grade trim enamel for a smooth-to-the-touch finish</li>
										</ul>
										<div className="time-expectation"><strong>Time expectation:</strong> 3 days (15 hours). Extended drying times required between cleaning and painting, and between enamel coats</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Materials estimate</h3>
									<div className="white-card">
										<ul className="material-card-list">
											<li>Wall paint: $120</li>
											<li>Bonding primer: $30</li>
											<li>Trim enamel for handrail: $15</li>
											<li>Misc consumables: $35</li>
										</ul>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Pricing</h3>
									<div className="quote-pricing-block">
										<div className="pricing-row">
											<span className="pricing-label">Labor (flat rate):</span>
											<span className="pricing-value">$750</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials:</span>
											<span className="pricing-value">~$200</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">$950</span>
										</div>
									</div>
									<div className="pricing-note">Materials are billed at exact cost.</div>
								</div>

							</div>
						</Accordion>

						<Accordion title="Project 5: Front Entry Room Shiplap / Wainscoting" onOpen={trackAccordionOpen}>
							<div className="quote-section">

								<div className='quote-subsection'>
									<div className="project-description">
										We discussed two options for the front room walls- horizontal shiplap or classic wainscoting panels. Both would run along the left wall, right wall, and the bisecting wall elements. Here's a breakdown of both so you can decide what's right for the space.
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Option comparison</h3>

									<div className="material-choice-grid">
										<div className="white-card">
											<div className="white-card-title">Shiplap - engineered pine</div>
											<div className="white-card-comment"><a href="https://www.homedepot.com/p/ARAUCO-9-16-in-x-5-1-4-in-x-16-ft-Radiata-Pine-Nickel-Gap-Ship-Lap-Board-1189986/300803828" target="_blank">Horizontal nickel-gap primed pine boards</a> running 56" up the wall. Covers all surfaces including the center post, the short connecting wall, and the angled stairwell section. Comes pre-milled with the nickel-gap profile, so no board milling required.</div>
											<ul className="option-card-list">
												<li><strong>Coverage:</strong> ~224 sq ft across all surfaces</li>
												<li><strong>Height:</strong> 56", fills the angled stairwell section completely</li>
												<li><strong>Includes:</strong> post wrapping with mitered corners, short connecting wall, cap trim throughout</li>
											</ul>
										</div>
										<div className="white-card">
											<div className="white-card-title">Shiplap - solid wood</div>
											<div className="white-card-comment">Same horizontal nickel-gap layout using solid hardwood boards. Requires milling a rabbet on each board to create the nickel-gap profile, plus a full finishing schedule before install. Denser surface than engineered pine and holds up better over decades.</div>
											<ul className="option-card-list">
												<li><strong>Coverage:</strong> ~224 sq ft across all surfaces</li>
												<li><strong>Height:</strong> 56", fills the angled stairwell section completely</li>
												<li><strong>Includes:</strong> post wrapping with mitered corners, short connecting wall, cap trim throughout</li>
												<li><strong>Tradeoff:</strong> Meaningfully more labor-intensive due to board milling and full finish schedule</li>
											</ul>
										</div>
										<div className="white-card">
											<div className="white-card-title">Wainscoting panels</div>
											<div className="white-card-comment" style={{ fontSize: '1.5rem' }}>Classic raised or flat panel wainscoting at standard 36-48" height. Due to the angled stairwell wall and the narrow post dimensions, wainscoting would be limited to the two main walls only. The center post, short connecting wall, and angled section on the stairs would be left plain.</div>
											<a href="https://www.homedepot.com/p/Ekena-Millwork-40-in-H-x-94-1-2-in-W-26-24-sq-ft-Beadboard-PVC-Wainscot-Paneling-Kit-WPK40X94BBD/324807038" target="_blank">Example 1</a><span> </span>
											<a href="https://www.homedepot.com/p/Ekena-Millwork-44-in-H-x-94-1-2-in-W-28-92-sq-ft-Framed-Beadboard-PVC-Wainscot-Paneling-Kit-WPK44X94FBD/324807052" target="_blank">Example 2</a><div />
											<ul className="option-card-list">
												<li><strong>Coverage:</strong> ~122 sq ft on main walls only</li>
												<li><strong>Height:</strong> 36-48", leaves angled stairwell section unfinished</li>
												<li><strong>Excludes:</strong> post, short connecting wall, angled section</li>
											</ul>
										</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Scope of work</h3>
									<div className="material-choice-grid">
										<div className="white-card">
											<div className="white-card-title">If shiplap - engineered pine:</div>
											<ul className="scope-list" style={{ marginTop: 0 }}>
												<li><strong>Layout:</strong> Mark level lines and plan board courses so they're consistent across all surfaces and corners</li>
												<li><strong>Installation:</strong> Install horizontal nickel-gap shiplap across all surfaces with mitered corners on the post and stairwell angle</li>
												<li><strong>Cap trim:</strong> Install and finish a chair-rail style cap along all top edges</li>
												<li><strong>Finishing:</strong> Fill all nail holes, caulk all edges and corners, sand, prime, and paint</li>
												<li><strong>Cleanup:</strong> Full cleanup and haul away all cutoffs and debris</li>
											</ul>
											<div className="time-expectation"><strong>Time expectation:</strong> 5-6 days (40-50 hours), drying time required</div>
										</div>
										<div className="white-card">
											<div className="white-card-title">If shiplap - solid wood:</div>
											<p>This follows the same structural scope as the pine, but includes a <strong>Furniture-Grade Finishing Schedule</strong>:</p>
											<ul className="scope-list" style={{ marginTop: 0 }}>
												<li><strong>Custom Milling:</strong> Two-pass milling of each raw board to create the nickel-gap profile, followed by hand-sanding of all routed edges</li>
												<li><strong>Pre-Installation Prep:</strong> Initial sanding, plus two coats of bonding primer with intermediate sanding passes to level the grain</li>
												<li><strong>Finish:</strong> The surface undergoes a multi-stage sanding progression, including a final 320-grit leveling pass before the final topcoats.</li>
											</ul>
											<div className="time-expectation"><strong>Time expectation:</strong> 7-8 days (55-65 hours). *Reflects the intensive manual labor required for hardwood finishing.*</div>
										</div>
										<div className="white-card">
											<div className="white-card-title">If wainscoting:</div>
											<ul className="scope-list" style={{ marginTop: 0 }}>
												<li><strong>Layout:</strong> Mark level lines and plan panel placement across both main walls</li>
												<li><strong>Installation:</strong> Install raised or flat panel wainscoting kits across main walls only</li>
												<li><strong>Cap trim:</strong> Install and finish chair-rail style cap along all top edges</li>
												<li><strong>Finishing:</strong> Fill all seams, caulk all edges and corners, sand, prime, and paint</li>
												<li><strong>Cleanup:</strong> Full cleanup and haul away all packaging and debris</li>
											</ul>
											<div className="time-expectation"><strong>Time expectation:</strong> 2-3 days (20-25 hours)</div>
										</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Materials estimate</h3>
									<div className="white-card">
										<ul className="material-card-list">
											<li>Shiplap - engineered pine: <a href="https://www.homedepot.com/p/ARAUCO-9-16-in-x-5-1-4-in-x-12-ft-Radiata-Pine-Nickel-Gap-Ship-Lap-Board-1186661/300803814" target="_blank">$550 - $650</a></li>
											<li>Shiplap - poplar: <a href="https://www.homedepot.com/p/Poplar-Board-Common-1-in-x-6-in-x-R-L-Actual-0-75-in-x-5-5-in-x-R-L-21065/302006547" target="_blank">$1,900 - $2,100</a></li>
											<li>Shiplap - maple: <a href="https://www.homedepot.com/p/Swaner-Hardwood-1-in-x-6-in-x-8-ft-Maple-S4S-Board-2-Pack-OL04051696ME/310362079" target="_blank">$2,900 - $3,300</a></li>
											<li>Wainscoting panel kits (~122 sqft): $1,300 - $1,600</li>
											<li>Cap/chair rail trim: $40 - $200</li>
											<li>Construction adhesive + finish nails: $25</li>
											<li>Primer + paint: $60 - $150</li>
											<li>Misc (sandpaper, caulk, wood filler): $20</li>
										</ul>
									</div>
								</div>
								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Pricing</h3>
									<div className="quote-pricing-block">
										<b>Shiplap - engineered pine</b>
										<div className="pricing-row">
											<span className="pricing-label">Labor (flat rate):</span>
											<span className="pricing-value">$2,750</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials estimate:</span>
											<span className="pricing-value">~$750</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">~$3,500</span>
										</div>
									</div>
									<div className="quote-pricing-block">
										<b>Shiplap - solid wood</b>
										<div className="pricing-row">
											<span className="pricing-label">Labor (flat rate):</span>
											<span className="pricing-value">$3,575</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials range:</span>
											<span className="pricing-value">$2,245 - $3,695</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">$5,820 - $7,270</span>
										</div>
									</div>
									<p>Actual material cost will be based on wood species, such as poplar, birch, etc.</p>
									<div className="quote-pricing-block">
										<b>Wainscoting</b>
										<div className="pricing-row">
											<span className="pricing-label">Labor (flat rate):</span>
											<span className="pricing-value">$1,375</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials range:</span>
											<span className="pricing-value">$1,445 - $1,785</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">$2,820 - $3,160</span>
										</div>
									</div>
									<div className="pricing-note">Pricing reflects a meticulous, furniture-grade finish on all visible surfaces that leaves no nail holes, seams, or lap marks. This is a fully wrapped, custom architectural treatment, not a surface-level install. Materials are billed at exact cost. Final cost depends on options selected.</div>
								</div>

							</div>
						</Accordion>

						<Accordion title="Project 6: Garage Pilates Studio" onOpen={trackAccordionOpen}>
							<div className="quote-section">

								<div className='quote-subsection'>
									<div className="project-description">
										The plan is to remove the existing mirrors, raise them to form a top row, and install a second row below, using three of the unused mirrors to create a run that closely matches the top row in width. A 16ft barre will be mounted between the two rows.<br /><br />
										Working with mirrors of this size is a careful, two-person job requiring proper glass-handling technique throughout and I'll treat it accordingly.
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Mirror layout</h3>
									<div className="white-card">
										<ul className="material-card-list">
											<li><strong>Top row</strong> (existing mirrors, relocated higher): 60" + 51" + 44" + 41" = <strong>196" total</strong></li>
											<li><strong>Bottom row</strong> (from unused mirrors): 94" + 75" + 24" = <strong>193" total</strong></li>
										</ul>
										<div className="white-card-comment">The two rows are within 3" of each other in total width, which will look balanced when centered. Note that the bottom row mirrors are slightly different heights (42", 40", and 38" tall), so the bottom edges will have a small natural stagger. This shouldn't be noticeable when in use.</div>
										<div className="white-card-comment">The remaining mirrors (41"x37", 48"x42", and three 24"x38") are not part of this installation. Let me know if you'd like to discuss using them elsewhere.</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Scope of work</h3>
									<div className="white-card">
										<ul className="scope-list">
											<li><strong>Mirror Removal:</strong> Carefully uninstall all four existing wall mirrors</li>
											<li><strong>Layout & Leveling:</strong> Map the wall, locate studs, and establish level lines for both rows and the barre</li>
											<li><strong>Hardware Assessment:</strong> Measure glass thickness on the bottom row mirrors and determine appropriate J-channel sizing or clip configuration for frameless installation</li>
											<li><strong>Alignment (Surface True-ing):</strong> Precision shimming behind each glass panel to compensate for typical garage wall irregularities. This creates a perfectly flat mounting plane, ensuring crisp, undistorted reflections (no "funhouse" effect) across the 16ft span</li>
											<li><strong>Top Row Installation:</strong> Rehang the four original mirrors at the new height using J-channel or heavy-duty mirror clips</li>
											<li><strong>Bottom Row Installation:</strong> Mount the three floor mirrors below, flush with the top row centerline</li>
											<li><strong>Barre Installation:</strong> Mount brackets into studs and install the 16ft wooden barre between the two mirror rows</li>
										</ul>
										<div className="time-expectation"><strong>Time expectation:</strong> 8-10 hours. Large-format frameless glass requires surgical precision. We'll spend the first few hours on a high-accuracy layout and removal, ensuring the wall is perfectly prepped before the glass ever moves. Requires two people for all lifting and positioning and cannot be rushed.</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Barre options</h3>
									<div className="white-card">
										<div className="white-card-comment">Since you already have the wooden rod, the only decision is how to mount it. We need 5 or 6 brackets for the 16' span, depending on the bracket type. There are a million options online. Here are two I found just for pricing reference:</div>

										<div><a href="https://a.co/d/03KHCj1O" target="_blank">Would need 3 pairs: $174 total</a></div>
										<div><a href="https://vitabarre.com/geo-wall-mount-ballet-barre-brackets/" target="_blank">Would need 5 of these: $350 total</a></div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<div className="white-card">
										<div className="white-card-title">Required Essentials</div>
										<ul className="material-card-list">
											<li><strong>Mirror J-channel or heavy-duty mirror clips:</strong> ~$60</li>
											<li><strong>Structural fasteners & shims:</strong> ~$25</li>
										</ul>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Pricing</h3>
									<div className="quote-pricing-block">
										<div className="pricing-row">
											<span className="pricing-label">Labor (flat rate):</span>
											<span className="pricing-value">$975</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials range:</span>
											<span className="pricing-value">$259 - $435</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">$1,234 - $1,410</span>
										</div>
									</div>
									<div className="pricing-note">Materials are billed at exact cost. Final cost depends on barre bracket selection. Labor reflects a two-person crew for safe handling of large-format glass throughout the install.</div>
								</div>

							</div>
						</Accordion>

						<Accordion title="Project 7: Clint's Closet" onOpen={trackAccordionOpen}>
							<div className="quote-section">

								<div className='quote-subsection'>
									<div className="project-description">
										Clint's closet has three wire shelves running along each wall. The left wall has that prefab shelving unit that is allowing you to utilize that entire wall space, but the right wall is only making half the use it could be. The shelf on the right is short (it stops where the back-wall shelf begins) and there's no room underneath it for a second hanging rod.
										<br /><br />
										The fix is straightforward: raise that shelf high enough to create double-hang clearance, then add a second shelf below it that runs the full depth of the right wall. That new lower shelf can reach all the way to the back corner, which the current one can't, adding usable space that's going to waste right now.
										<br /><br />
										Net result: the right wall gains an additional full-length shelf and hanging rod, more than doubling the capacity.
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Scope of work</h3>
									<div className="white-card">
										<ul className="scope-list">
											<li><strong>Shelf Removal:</strong> Take down the existing right-wall wire shelf and hardware</li>
											<li><strong>Wall Repair:</strong> Patch and touch up the holes from the previous mounting hardware</li>
											<li><strong>Reinstall / Replace Upper Shelf:</strong> Rehang at a raised height to allow double-hang clearance below</li>
											<li><strong>New Lower Shelf:</strong> Install a second shelf at standard hang height running the full depth of the right wall, extending into the back corner</li>
											<li><strong>Finishing:</strong> Level, secure, clean up, and haul away any debris</li>
										</ul>
										<div className="time-expectation"><strong>Time expectation:</strong> 3-4 hours.</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Material options</h3>

									<div className="material-choice-grid">
										<div className="white-card">
											<div className="white-card-title">Option A: Reuse existing wire shelf</div>
											<div className="white-card-comment">The existing right-wall shelf gets relocated to the new upper position, and I'll source a matching wire shelf for the lower run. Keeps the closet consistent with the other two walls and is the most economical choice.</div>
											<div className="white-card-comment">New wire shelf (44"): ~$45</div>
										</div>
										<div className="white-card">
											<div className="white-card-title">Option B: New pine shelves (both levels)</div>
											<div className="white-card-comment">Replace the existing wire shelf and add the new one, both in primed pine or melamine. Same material choices apply as Project 1.</div>
											<div className="white-card-comment">Primed pine: ~$30 + paint<br />Melamine: ~$25<br />Also 2 hanging rods & hardware</div>
										</div>
										<div className="white-card">
											<div className="white-card-title">Option C: New solid wood shelves (both levels)</div>
											<div className="white-card-comment">Replace the existing wire shelf and add the new one, both in custom poplar similar to the downstairs closet.</div>
											<div className="white-card-comment">Poplar: ~$110 + paint<br />Also 2 hanging rods & hardware</div>
										</div>
									</div>

									<div className="subsection-comment">If there's any chance the other two walls might get updated down the road, I'd go with wood now so everything eventually matches. If the goal is just to solve the storage problem quickly and efficiently, the wire option is perfectly functional.</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<div className="white-card">
										<div className="white-card-title">Required Essentials</div>
										<ul className="material-card-list">
											<li><strong>Rod Flanges & Structural Hardware:</strong> Heavy-duty end caps and 3" structural screws for stud mounting</li>
											<li><strong>Drywall patching:</strong> Any holes left from the relocated shelf hardware</li>
										</ul>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Pricing</h3>
									<div className="quote-pricing-block">
										<div className="pricing-row">
											<span className="pricing-label">Labor - 1 wire shelf (flat rate):</span>
											<span className="pricing-value">$200</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Labor - 2 pine shelves (flat rate):</span>
											<span className="pricing-value">$275</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Labor - 2 poplar shelves (flat rate):</span>
											<span className="pricing-value">$400</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials range:</span>
											<span className="pricing-value">$60 - $320</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">$260 - $720</span>
										</div>
									</div>
									<div className="pricing-note">Materials are billed at exact cost. Option A includes one new shelf; Options B and C include replacing both right-wall shelves with engineered pine or solid poplar</div>
								</div>

							</div>
						</Accordion>

						<Accordion title="Project 8: Cayla's Closet" onOpen={trackAccordionOpen}>
							<div className="quote-section">

								<div className='quote-subsection'>
									<div className="project-description">
										Cayla's closet is doing a lot with a little, but two changes will make a meaningful difference. First, the back wall shelf gets raised to give the clothes hanging from it more breathing room. This frees up the top of the dresser to actually become usable surface. (Even if the top of the dresser isn't used for storage, this will still seriously un-cramp the space).
										<br /><br />
										Second, we address Shoe Mountain on the left side of the floor. I'll solve this with a custom floating shoe shelf system installed on the right interior wall- turning what's currently just dresser-drawer clearance into dedicated shoe storage and clearing the left side floor entirely.
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Scope of work</h3>
									<div className="white-card">
										<ul className="scope-list">
											<li><strong>Back Shelf Raise:</strong> Remove the existing back-wall wire shelf and reinstall it several inches higher, giving the hanging clothes proper clearance and freeing up the top of the dresser</li>
											<li><strong>Shoe Shelf Build & Install:</strong> Build and mount a custom floating shelf system on the right interior wall. 12 to 14 shelves, 21" wide and 12" deep, bracketed directly into the wall with no side panels so the doorway sightline stays open</li>
											<li><strong>Shoe Migration:</strong> The shoe racks currently on the left floor can be removed once the new shelving is in place, opening up the left side entirely</li>
											<li><strong>Finishing:</strong> Patch any holes from the relocated back shelf hardware, level and secure all new shelves, clean up and haul away debris</li>
										</ul>
										<div className="time-expectation"><strong>Time expectation:</strong> 2 days (8-12 hours), overnight required for drying</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Why raise the back shelf?</h3>
									<div className="white-card">
										<div className="white-card-comment">Right now the clothes hanging on the back wall are crammed tight. There's no clearance between the hem of the lowest garments and the top of the dresser, which means the dresser top is essentially unusable and everything feels compressed. Raising the shelf by about 12" solves both problems: the clothes hang freely, and the dresser top becomes a real surface again. The shelf will be slightly higher than ideal for a 6 year old to reach on her own, but that's a temporary problem that will eventually solve itself, and the dresser is right there to step on in the meantime :)</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Why floating shelves instead of a freestanding unit?</h3>
									<div className="white-card">
										<div className="white-card-comment">A freestanding shoe cabinet would put a 12" deep wall of wood right at the edge of the door opening, cutting into the sightline and making the closet feel smaller than it already is. Floating shelves are secured directly into the wall so they will keep the right alcove visually open.</div>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Material options</h3>

									<div className="material-choice-grid">
										<div className="white-card">
											<div className="white-card-title">Option A: Wire shelf (back wall only)</div>
											<div className="white-card-comment">The existing back-wall wire shelf gets relocated to the new height. For the shoe shelves, wood is the only practical option since wire shelving doesn't work well for shoes at custom spacing.</div>
											<div className="white-card-comment">Back shelf relocation: no new material cost<br />Shoe shelves (wood): $50-60 in lumber</div>
										</div>
										<div className="white-card">
											<div className="white-card-title">Option B: Pine shelves (both)</div>
											<div className="white-card-comment">Replace the back-wall wire shelf with a wood shelf to match the shoe shelves and give the whole closet a consistent, finished look. Same primed pine or melamine choices as Project 1.</div>
											<div className="white-card-comment">Primed pine: ~$15 + paint<br />Melamine: ~$12<br />Shoe shelves (wood): $50-60 in lumber</div>
										</div>
										<div className="white-card">
											<div className="white-card-title">Option C: Solid poplar (both)</div>
											<div className="white-card-comment">Both the back shelf and all shoe shelves in solid poplar with the same furniture-grade finishing schedule as the guest room closet. Two coats primer, sanding between coats, two finish coats, final 320-grit pass.</div>
											<div className="white-card-comment">Poplar (back shelf + shoe shelves): ~$120-140 + paint & primer (~$30)</div>
										</div>

									</div>

									<div className="subsection-comment">The shoe shelves must be some form of wood. Matching the back shelf to them is a small upcharge for a much more cohesive result.</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<div className="white-card">
										<div className="white-card-title">Required Essentials</div>
										<ul className="material-card-list">
											<li><strong>Shelf brackets (24-28):</strong> Two per shelf, rated for the load. $60-80 total</li>
											<li><strong>Drywall patching:</strong> Holes from the relocated back shelf hardware</li>
										</ul>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Pricing</h3>
									<div className="quote-pricing-block">
										<div className="pricing-row">
											<span className="pricing-label">Labor - reuse wire shelf (flat rate):</span>
											<span className="pricing-value">$390</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Labor - 2 pine shelves (flat rate):</span>
											<span className="pricing-value">$520</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Labor - 2 poplar shelves (flat rate):</span>
											<span className="pricing-value">$650</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials range:</span>
											<span className="pricing-value">$110 - $280</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">$500 - $930</span>
										</div>
									</div>
									<div className="pricing-note">Option C reflects the full furniture-grade finishing schedule used in the guest room closet, applied to both the back shelf and all shoe shelves. Materials are billed at exact cost.</div>
								</div>

							</div>
						</Accordion>
						<Accordion title="Project 9: Fence Gate" onOpen={trackAccordionOpen}>
							<div className="quote-section">

								<div className='quote-subsection'>
									<div className="project-description">
										This project involves modifying the existing 67-inch vinyl fence span to install a 48-inch pedestrian gate. To ensure long-term durability and prevent the common sagging issues associated with wide vinyl gates, I will be reinforcing the hinge post with an internal aluminum I-beam stiffener and a deep concrete footer. The remaining gap will be closed with a custom-fabricated ~15 inch fixed mini-panel for a seamless finish.
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Scope of work</h3>
									<div className="white-card">
										<ul className="scope-list">
											<li><strong>Structural Reinforcement:</strong> Install a 5x5 aluminum stiffener into the hinge post and pour a dedicated concrete footer to support the gate's weight.</li>
											<li><strong>Post Installation:</strong> Set a new 5x5 white vinyl blank post in concrete at the precise rough opening required for the gate hardware.</li>
											<li><strong>Gate Fabrication:</strong> Assemble and hang a 48-inch wide gate kit, ensuring all horizontal rails are properly seated in structural corner sockets.</li>
											<li><strong>Infill Panel:</strong> Custom-cut the existing 67-inch rails to create a 15-inch fixed panel; install with white U-channel caps for a finished look.</li>
											<li><strong>Post Routing:</strong> Precision-route the new blank post to accept the horizontal rails of the infill panel.</li>
											<li><strong>Hardware & Tuning:</strong> Mount heavy-duty stainless steel self-closing hinges and a lockable gravity latch. Adjust tension for consistent, secure closure.</li>
										</ul>
										<div className="time-expectation"><strong>Time expectation:</strong> 2 days (9-10 hours), overnight required for concrete curing</div>
									</div>
									<div className="subsection-comment">
										Note: This quote assumes the existing posts are currently plumb. If significant correction of the existing fence line is required to achieve a level gate swing, additional labor may be required.
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Materials estimate</h3>
									<div className="white-card">
										<ul className="material-card-list">
											<li>48" white vinyl gate: $117</li>
											<li>5x5 x 96" white vinyl blank post: $18</li>
											<li>5x5 aluminum post stiffener: $50</li>
											<li>Heavy-duty hardware kit: $70</li>
											<li>U-channel caps & gate sockets: $45</li>
											<li>Concrete & misc consumables: $35</li>
										</ul>
									</div>
								</div>

								<hr />

								<div className='quote-subsection'>
									<h3 className="section-label">Pricing</h3>
									<div className="quote-pricing-block">
										<div className="pricing-row">
											<span className="pricing-label">Labor (flat rate):</span>
											<span className="pricing-value">$550</span>
										</div>
										<div className="pricing-row">
											<span className="pricing-label">Materials:</span>
											<span className="pricing-value">~$335</span>
										</div>
										<div className="pricing-row pricing-row--total">
											<span className="pricing-label">Estimated total:</span>
											<span className="pricing-value">$885</span>
										</div>
									</div>
									<div className="pricing-note">Materials are billed at exact cost. Gate materials provided by Tru-fence Supply, who is the supplier for Wolfe Fencing, ensuring an exact match to the existing fence.</div>
								</div>

							</div>
						</Accordion>
						<hr style={{ marginTop: '3rem' }} />
						<h2>Ready when you are</h2>
						<p>
							For any project that requires a significant material order, I'll collect the material cost upfront before purchasing anything. Labor is collected at completion.
						</p>
						<p>
							Just text me whenever you've had a chance to look this over, or if any questions come up. When you're ready to move forward I'll email a formal quote to review and sign.
						</p>
					</div>
				</section >
			</main >
			<footer className="quote-footer">
				<h1>Thank you!</h1>
				<div className="footer-brand-box">
					<img
						src="/transparent-logo-min.png"
						alt="North Florida Home Repair & Maintenance"
						className="footer-logo"
					/>
					<div className="footer-fine-print">
						Reliable | Insured | Professional Home Services
					</div>
				</div>
				<div className="footer-contact-info">
					<a href="tel:9043862663" className="contact-link">
						(904) 386-2663
					</a>
					<span className="contact-divider">•</span>
					<a href="mailto:daniel@northfloridarepair.com" className="contact-link">
						daniel@northfloridarepair.com
					</a>
				</div>

				<div className="footer-actions">
					<button onClick={() => navigate('/')} className='home-button'>
						← Return Home
					</button>
				</div>
			</footer>
		</div >
	);
}

export default V5rw6uja5;